import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { Typography } from '@mui/material';
import { ImageSquare } from '@phosphor-icons/react';
import { useSelector } from 'react-redux';

import { PsTextTuner, PsButtonTuner } from 'stencilSetup';
import { FormElement } from 'enrollment/components/Shared/FormElement';
import { BenefitDto } from 'enrollment/queries/useBenefitCategoryQuery/types';
import {
  InputWrapperStyled,
  CheckCircleStyled,
  SuccessTextStyled,
} from './ESignatureBoxStyled';
import { selectIsAdmin } from 'theSource/features';

interface ESignatureBoxProps {
  esignBenefit: BenefitDto;
  submitEsign: (
    esignBenefit: BenefitDto,
    password?: string,
    checked?: boolean
  ) => void;
  disabled: (planId: number) => boolean;
}

export const ESignatureBox = ({
  esignBenefit,
  submitEsign,
  disabled,
}: ESignatureBoxProps) => {
  const { t } = useTranslation('enrollment.esign');
  const isAdmin = useSelector(selectIsAdmin);

  const coverage = esignBenefit.coverage;
  const carrierLogoUrl = coverage?.carrier_logo_url;
  const carrierName = coverage?.req_carrier_name;
  const esignData = coverage?.esign_data;
  const esigned = coverage?.esign_data?.esigned;
  const isPasswordEsign =
    esignData?.signature_type?.toLowerCase() === 'password';

  let esignText = '';
  if (isAdmin && Boolean(esignData?.admin_esign_text)) {
    esignText = esignData?.admin_esign_text || '';
  } else {
    esignText = esignData?.esign_text || '';
  }

  const [esignPassword, setEsignPassword] = useState<string | undefined>(
    undefined
  );

  const handleEsignPassword = (event: any) => {
    const password = String(event.target.value) || '';
    setEsignPassword(password);
  };

  const submitEsignPassword = () => {
    submitEsign(esignBenefit, esignPassword);
  };

  const handleEsignCheckbox = (event: any) => {
    const checked = event.target.checked;
    submitEsign(esignBenefit, undefined, checked);
  };

  if (!coverage) {
    return <></>;
  }

  const planId = coverage.req_org_plan_id;
  const disable = disabled(planId);

  return (
    <div data-testid={`esign-${coverage?.coverage_id}`}>
      <div className="pb-3">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h3>
            {esignBenefit.benefit.org_benefit_name}:{' '}
            {coverage.req_org_plan_name}
          </h3>
          {carrierLogoUrl !== null ? (
            <img
              src={carrierLogoUrl}
              alt={carrierName}
              data-testid="carrier-logo"
            />
          ) : (
            <div className="d-flex align-items-center">
              <ImageSquare size={32} weight="duotone" color="#56638a" />
              <Typography color="#56638a">{carrierName}</Typography>
            </div>
          )}
        </div>

        {esignText && (
          <div className="border rounded p-3 mb-3">
            <Typography variant="body1">{esignText}</Typography>
          </div>
        )}

        <InputWrapperStyled>
          {isPasswordEsign ? (
            <div>
              <PsTextTuner
                data-testid="esignature-password"
                inputId="esignature-password"
                name="esignature_password"
                label="Password"
                type="password"
                value={esigned ? '********' : ''}
                onInput={(e) => handleEsignPassword(e as any)}
                otherData={[
                  { attribute: 'autoComplete', value: 'off' },
                  { attribute: 'required-word', value: 'Required' },
                ]}
                allowUnmask
                extraClass="mb-3"
                disabled={disable}
                required
                validationText="Required"
              />
              {!esigned && (
                <PsButtonTuner
                  data-testid="submit-esignature"
                  buttonId="submit-esignature"
                  label={t('confirm')}
                  level="primary"
                  onClick={submitEsignPassword}
                  disabled={disable}
                />
              )}
            </div>
          ) : (
            <FormElement
              data-testid={`esignature-checkbox-${planId}`}
              formField="checkbox"
              inputId={`esignature-checkbox-${planId}`}
              label={
                esignData?.custom_text && esignData?.custom_text !== ' '
                  ? esignData?.custom_text
                  : t('termsConditionLabel')
              }
              extraClass="ml-0"
              value={esigned}
              checked={esigned}
              onChange={(e) => handleEsignCheckbox(e as any)}
              disabled={disable}
              requiredWord="Required"
              required={true}
            />
          )}
        </InputWrapperStyled>
        {esigned && (
          <div className="d-flex align-items-center">
            <CheckCircleStyled size={20} weight="bold" className="mr-1" />
            <SuccessTextStyled>
              {isPasswordEsign ? t('submitted') : t('saved')}
            </SuccessTextStyled>
          </div>
        )}
      </div>
      <hr className="mx-2" />
    </div>
  );
};
